import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Notice } from '../types/notice';

interface NoticeDetailModalProps {
  show: boolean;
  notice: Notice | null;
  onClose: () => void;
}

const NoticeDetailModal: React.FC<NoticeDetailModalProps> = ({ show, notice, onClose }) => {
  console.log('NoticeDetailModal render - show:', show, 'notice:', notice?.title);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (show) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [show]);

  if (!show || !notice) return null;

  const formattedDate = new Date(notice.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ 
          zIndex: 1040,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
      ></div>
      
      {/* Modal */}
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        style={{ 
          display: show ? 'block' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          paddingRight: show ? '17px' : '0',
          overflow: 'auto'
        }}
        tabIndex={-1}
        aria-labelledby="noticeDetailModalLabel"
        aria-hidden={!show}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" style={{ margin: '1rem' }}>
          <div className="modal-content card-surface" style={{ zIndex: 1051, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header border-bottom pb-3 flex-shrink-0">
              <h5 className="modal-title fw-bold h5 mb-0 d-flex align-items-center gap-2" id="noticeDetailModalLabel" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
                <i className="bi bi-megaphone-fill text-primary" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}></i>
                Notice Details
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                style={{ minWidth: '44px', minHeight: '44px' }}
              ></button>
            </div>
            <div className="modal-body p-3 p-md-4 flex-grow-1 overflow-auto">
              {/* Date Badge */}
              <div className="mb-3">
                <span className="badge bg-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                  <i className="bi bi-calendar3 me-2"></i>
                  {formattedDate}
                </span>
              </div>

              {/* Title */}
              <h4 className="mb-3 text-white fw-bold" style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', lineHeight: '1.4' }}>
                {notice.title}
              </h4>

              {/* Description */}
              <div className="mb-3">
                <h6 className="text-muted-soft mb-2" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  Description:
                </h6>
                <p 
                  className="text-white mb-0" 
                  style={{ 
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', 
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}
                >
                  {notice.description}
                </p>
              </div>

              {/* Additional Info */}
              {notice.createdAt && (
                <div className="mt-4 pt-3 border-top border-secondary">
                  <small className="text-muted-soft">
                    <i className="bi bi-clock me-1"></i>
                    Created: {new Date(notice.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
              )}
            </div>
            <div className="modal-footer border-top d-flex flex-column flex-sm-row gap-2 flex-shrink-0 p-3 p-md-4">
              <button 
                type="button" 
                className="btn btn-primary flex-fill w-100 w-sm-auto" 
                onClick={onClose}
              >
                <i className="bi bi-x-circle me-1"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Use React Portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default NoticeDetailModal;
